import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Grid, Chip, Button, Typography, alpha } from "@mui/material";
import {
  Add,
  Remove,
  DirectionsBike,
  PedalBike,
  FilterAlt,
  CheckCircle,
  Cancel,
} from "@mui/icons-material";
import MDBox from "shared/components/MDBox";
import MDTypography from "shared/components/MDTypography";
import DataTable from "shared/components/Tables/DataTable";
import GanchoCard from "./GanchoCard";
import GanchoDetailDialog from "./GanchoDetailDialog";
import { prkgColumns } from "../tables/tableConfig";

const movementButtonSx = {
  color: "#ffffff !important",
  px: 5,
  py: 1.5,
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "0.95rem",
  letterSpacing: 1,
  "& .MuiButton-startIcon": { color: "#ffffff" },
};

export default function ParkingOverview({
  ganchos,
  totalDisponibles,
  totalOcupados,
  parqueaderoActual,
  onIngreso,
  onSalida,
}) {
  const [filter, setFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const validGanchos = ganchos.filter(Boolean);
  const visibleGanchos = validGanchos.filter(
    (gancho) =>
      filter === "all" || (filter === "available" ? gancho.disponible : !gancho.disponible)
  );
  const selectedGancho =
    validGanchos.find((gancho) => gancho.id === selectedId && !gancho.disponible) || null;
  useEffect(() => {
    if (!selectedGancho && selectedId !== null) setSelectedId(null);
  }, [selectedGancho, selectedId]);
  const prkgRows = parqueaderoActual.map((item, idx) => ({
    id: item?.id || idx + 1,

    // datos del usuario
    id_user: item?.id_user,
    nombre: item?.nombre || "",
    cedula: item?.cedula || "",
    celular: item?.celular || "",

    // datos de la bicicleta
    id_byke: item?.id_byke,
    code: item?.code || "",
    color: item?.color || "",
    marca: item?.marca || "",
    bikeAssociationNeedsReview: item?.bikeAssociationNeedsReview || false,

    // otros
    harness: item?.harness || null,
    enter_at: item?.enter_at || null,
  }));

  return (
    <Box>
      <GanchoDetailDialog gancho={selectedGancho} onClose={() => setSelectedId(null)} />
      <Box sx={{ p: 2 }}>
        {/* ESTADO DE GANCHOS */}
        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: alpha("#1a237e", 0.04),
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item xs={12} md={6}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <FilterAlt sx={{ color: "#1a237e", fontSize: 20 }} />
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1a237e" }}>
                      Estado de Ganchos
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      {ganchos.length} ganchos totales
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    label={`${totalDisponibles} Disponibles`}
                    sx={{
                      bgcolor: "#e8f5e9",
                      color: "#2e7d32",
                      fontWeight: 600,
                      height: 24,
                      fontSize: "0.7rem",
                    }}
                    icon={<CheckCircle />}
                  />
                  <Chip
                    size="small"
                    label={`${totalOcupados} Ocupados`}
                    sx={{
                      bgcolor: "#fff4e3",
                      color: "#8b5417",
                      fontWeight: 600,
                      height: 24,
                      fontSize: "0.7rem",
                    }}
                    icon={<Cancel />}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ p: 1.5 }}>
            <Box
              role="group"
              aria-label="Filtrar ganchos por disponibilidad"
              sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 1.5 }}
            >
              {[
                ["all", `Todos (${validGanchos.length})`],
                ["available", `Disponibles (${totalDisponibles})`],
                ["occupied", `Ocupados (${totalOcupados})`],
              ].map(([value, label]) => (
                <Button
                  key={value}
                  size="small"
                  variant="outlined"
                  aria-pressed={filter === value}
                  onClick={() => setFilter(value)}
                  sx={{
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: filter === value ? "#fff" : "#245a83",
                    bgcolor: filter === value ? "#245a83" : "#fff",
                    borderColor: "#b9cfe0",
                    "&:hover": {
                      color: filter === value ? "#fff" : "#193e54",
                      bgcolor: filter === value ? "#193e54" : "#edf4fa",
                    },
                  }}
                >
                  {label}
                </Button>
              ))}
            </Box>
            <Typography variant="caption" sx={{ display: "block", mb: 1.5, color: "#526579" }}>
              El fondo indica disponibilidad; la muestra identifica el color de la bicicleta. Pulsa
              un gancho ocupado para ver su detalle.
            </Typography>
            {!visibleGanchos.length && (
              <Typography variant="body2" sx={{ p: 3, textAlign: "center", color: "#526579" }}>
                No hay ganchos para este filtro.
              </Typography>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                gap: 1,
                p: 1,
                bgcolor: "#fafafa",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {visibleGanchos.map((gancho) => (
                <GanchoCard key={gancho.id} gancho={gancho} onSelect={setSelectedId} />
              ))}
            </Box>
          </Box>
        </Box>

        {/* BOTONES DE ACCIÓN */}
        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
            mb: 3,
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: alpha("#1a237e", 0.04),
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <MDBox display="flex" alignItems="center" gap={1.5}>
              <PedalBike sx={{ color: "#1a237e", fontSize: 20 }} />
              <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1a237e" }}>
                Acciones
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary">
                Registrar ingreso o salida de bicicleta
              </MDTypography>
            </MDBox>
          </Box>

          <Box sx={{ p: 2.5, display: "flex", justifyContent: "center", gap: 3 }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<Add sx={{ fontSize: 26 }} />}
              onClick={() => onIngreso()}
              sx={{
                ...movementButtonSx,
                bgcolor: "#2e7d32",

                boxShadow: "0 4px 12px rgba(46, 125, 50, 0.3)",
                "&:hover": { bgcolor: "#1b5e20" },
              }}
            >
              INGRESO
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={<Remove sx={{ fontSize: 26 }} />}
              onClick={() => onSalida()}
              sx={{
                ...movementButtonSx,
                bgcolor: "#c62828",

                boxShadow: "0 4px 12px rgba(198, 40, 40, 0.3)",
                "&:hover": { bgcolor: "#b71c1c" },
              }}
            >
              SALIDA
            </Button>
          </Box>
        </Box>

        {/* PARQUEADERO ACTUAL */}
        <Box
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              p: 1.5,
              bgcolor: alpha("#1976d2", 0.06),
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between" spacing={1}>
              <Grid item xs={12} md={6}>
                <MDBox display="flex" alignItems="center" gap={1.5}>
                  <Box
                    sx={{
                      bgcolor: alpha("#1976d2", 0.12),
                      borderRadius: "8px",
                      p: 0.75,
                      display: "flex",
                    }}
                  >
                    <DirectionsBike sx={{ fontSize: 20, color: "#1976d2" }} />
                  </Box>
                  <MDBox>
                    <MDTypography variant="subtitle2" fontWeight="bold" sx={{ color: "#1976d2" }}>
                      Parqueadero Actual
                    </MDTypography>
                    <MDTypography variant="caption" color="text.secondary">
                      Bicicletas adentro en este momento
                    </MDTypography>
                  </MDBox>
                </MDBox>
              </Grid>
              <Grid item xs={12} md={6}>
                <MDBox
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="center"
                  gap={1}
                  flexWrap="wrap"
                >
                  <Chip
                    size="small"
                    label={`${parqueaderoActual.length} ${
                      parqueaderoActual.length === 1 ? "bicicleta" : "bicicletas"
                    } adentro`}
                    sx={{
                      bgcolor: alpha("#1976d2", 0.1),
                      color: "#1565c0",
                      fontWeight: 600,
                      height: 24,
                      fontSize: "0.7rem",
                    }}
                    icon={<CheckCircle />}
                  />
                </MDBox>
              </Grid>
            </Grid>
          </Box>

          {parqueaderoActual.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <DirectionsBike sx={{ fontSize: 48, color: "#cbd5e1", mb: 1 }} />
              <MDTypography variant="body2" color="text.secondary">
                No hay bicicletas en el parqueadero
              </MDTypography>
              <MDTypography variant="caption" color="text.secondary" display="block">
                Cuando registres un ingreso aparecerá aquí
              </MDTypography>
            </Box>
          ) : (
            <DataTable
              table={{ columns: prkgColumns, rows: prkgRows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
              canSearch={false}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
ParkingOverview.propTypes = {
  ganchos: PropTypes.array.isRequired,
  totalDisponibles: PropTypes.number.isRequired,
  totalOcupados: PropTypes.number.isRequired,
  parqueaderoActual: PropTypes.array.isRequired,
  onIngreso: PropTypes.func.isRequired,
  onSalida: PropTypes.func.isRequired,
};
